import React, {
 useState, useMemo
} from 'react';
import {
    Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from 'shared/components/ui/table';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Switch
} from 'shared/components/ui/switch';
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from 'shared/components/ui/tooltip';
import {
 Skeleton
} from 'shared/components/ui/skeleton';
import {
 Pencil, Trash2, Plus, Search, Zap
} from 'lucide-react';
import {
    TriggerListItem, DataChangeConfig, ScheduleConfig,
} from '../types/trigger.types';

interface TriggersTableProps {
    triggers: TriggerListItem[];
    isLoading: boolean;
    onEdit: (trigger: TriggerListItem) => void;
    onDelete: (trigger: TriggerListItem) => void;
    onToggleActive: (triggerId: string) => void;
    onNewTrigger: () => void;
}

function getEventSourceLabel(trigger: TriggerListItem): string {
    if (trigger.eventType === 'data_change') {
        const config = trigger.eventConfig as DataChangeConfig;
        return `On ${config.event} of '${config.schemaName}'`;
    }
    const config = trigger.eventConfig as ScheduleConfig;
    switch (config.frequency) {
        case 'every_minute': return 'Scheduled: Every minute';
        case 'every_hour': return 'Scheduled: Every hour';
        case 'every_day': return 'Scheduled: Every day';
        case 'custom': return `Scheduled: ${config.cronExpression || 'Custom'}`;
        default: return 'Scheduled';
    }
}

function getActionLabel(trigger: TriggerListItem): string {
    if (trigger.action.workflowName) {
        return `Executes '${trigger.action.workflowName}' workflow`;
    }
    return 'No action configured';
}

const TriggersTable: React.FC<TriggersTableProps> = ({
    triggers, isLoading, onEdit, onDelete, onToggleActive, onNewTrigger,
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredTriggers = useMemo(() => {
        if (!searchTerm) return triggers;
        return triggers.filter((t) =>
            t.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [triggers, searchTerm]);

    if (isLoading) {
        return (
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-44" />
                </div>
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[80px]">Status</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Event Source</TableHead>
                                <TableHead>Action</TableHead>
                                <TableHead>Last Fired</TableHead>
                                <TableHead className="w-[100px]">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({
 length: 5
}).map((_, i) => (
    <TableRow key={i}>
        <TableCell><Skeleton className="h-5 w-9" /></TableCell>
        <TableCell><Skeleton className="h-4 w-40" /></TableCell>
        <TableCell><Skeleton className="h-4 w-48" /></TableCell>
        <TableCell><Skeleton className="h-4 w-44" /></TableCell>
        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
        <TableCell><Skeleton className="h-8 w-20" /></TableCell>
    </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        );
    }

    return (
        <TooltipProvider>
            <div className="flex flex-col gap-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">Triggers</h1>
                        <p className="text-sm text-muted-foreground">Automate actions in response to events</p>
                    </div>
                    <Button onClick={onNewTrigger}>
                        <Plus className="mr-2 h-4 w-4" />
                        Create New Trigger
                    </Button>
                </div>

                {/* Search */}
                {triggers.length > 0 && (
                    <div className="flex gap-3 items-center">
                        <div className="relative flex-grow max-w-sm">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search triggers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                    </div>
                )}

                {/* Table or Empty State */}
                {filteredTriggers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-4 border rounded-md">
                        <div className="rounded-full bg-muted p-6">
                            <Zap className="h-10 w-10 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold">No triggers defined</h3>
                        <p className="text-sm text-muted-foreground text-center max-w-md">
                            Triggers automate actions in response to data changes or on a schedule. Create your first trigger to get started.
                        </p>
                        <Button onClick={onNewTrigger}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Your First Trigger
                        </Button>
                    </div>
                ) : (
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[80px]">Status</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Event Source</TableHead>
                                    <TableHead>Action</TableHead>
                                    <TableHead>Last Fired</TableHead>
                                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredTriggers.map((trigger) => (
                                    <TableRow key={trigger.id} className={!trigger.active ? 'opacity-60' : ''}>
                                        <TableCell>
                                            <Switch
                                                checked={trigger.active}
                                                onCheckedChange={() => onToggleActive(trigger.id)}
                                            />
                                        </TableCell>
                                        <TableCell className="font-medium">{trigger.name}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="text-xs font-normal">
                                                {getEventSourceLabel(trigger)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {getActionLabel(trigger)}
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {trigger.lastFired || '-'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8"
                                                            onClick={() => onEdit(trigger)}
                                                        >
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent><p>Edit</p></TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-red-600 hover:text-red-700"
                                                            onClick={() => onDelete(trigger)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent><p>Delete</p></TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </TooltipProvider>
    );
};

export default TriggersTable;
